import { v4 as uuidv4 } from "uuid";
import apiClient from "../../../../api/client";
import { BASE_URL } from "config/api";
import { sendSMS } from "../../../../api/external";

/** Format attendu par la BDD : YYYY-MM-DD (ex. 2025-04-17) */
const formatNotificationDate = (value) => {
  if (!value) {
    return new Date().toISOString().slice(0, 10);
  }

  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }

  const datePart = raw.split("T")[0].split(" ")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return datePart;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return new Date().toISOString().slice(0, 10);
};

const findUser = (users, userId) => {
  if (!userId) return null;
  const list = Array.isArray(users) ? users : [];
  return list.find((user) => String(user.id) === String(userId)) || null;
};

const buildAmbulancierMessage = (missionData, patient, doctor, nurse) => `Nouvelle mission:
Type: ${missionData.type_mission || "N/A"}
cause: ${missionData.cause || "N/A"}
Date: ${missionData.date_mission || "N/A"}
Adresse: ${missionData.adresse || "N/A"}
Demande Matériel: ${missionData.demande_materiel || "N/A"}

Patient:
${patient?.nom || "N/A"} ${patient?.prenom || "N/A"}
Téléphone: ${patient?.téléphone || "N/A"}

Médecin:
${doctor?.nom || "N/A"} ${doctor?.prenom || "N/A"}
Téléphone: ${doctor?.téléphone || "N/A"}

Infirmier:
${nurse?.nom || "N/A"} ${nurse?.prenom || "N/A"}
Téléphone: ${nurse?.téléphone || "N/A"}`;

const buildDoctorMessage = (missionData, patient, ambulancier, nurse) => `Nouvelle mission:
Type: ${missionData.type_mission || "N/A"}
cause: ${missionData.cause || "N/A"}
Date: ${missionData.date_mission || "N/A"}
Adresse: ${missionData.adresse || "N/A"} 
Demande Matériel: ${missionData.demande_materiel || "N/A"}

Patient:
${patient?.nom || "N/A"} ${patient?.prenom || "N/A"}
Téléphone: ${patient?.téléphone || "N/A"}

Ambulancier:
${ambulancier?.nom || "N/A"} ${ambulancier?.prenom || "N/A"}
Téléphone: ${ambulancier?.téléphone || "N/A"}

Infirmier:
${nurse?.nom || "N/A"} ${nurse?.prenom || "N/A"}
Téléphone: ${nurse?.téléphone || "N/A"}`;

const buildNurseMessage = (missionData, patient, ambulancier, doctor) => {
  if (missionData.type_mission === "Acte infirmier") {
    return `Nouvelle mission d'acte infirmier:
Type: ${missionData.type_mission || "N/A"}
cause: ${missionData.cause || "N/A"}
Date: ${missionData.date_mission || "N/A"}
Adresse: ${missionData.adresse || "N/A"}
Demande Matériel: ${missionData.demande_materiel || "N/A"}

Patient:
${patient?.nom || "N/A"} ${patient?.prenom || "N/A"}
Téléphone: ${patient?.téléphone || "N/A"}

Médecin:
${doctor?.nom || "N/A"} ${doctor?.prenom || "N/A"}
Téléphone: ${doctor?.téléphone || "N/A"}

Ambulancier:
${ambulancier?.nom || "N/A"} ${ambulancier?.prenom || "N/A"}
Téléphone: ${ambulancier?.téléphone || "N/A"}`;
  }

  return `Nouvelle mission:
Type: ${missionData.type_mission || "N/A"}
cause: ${missionData.cause || "N/A"}
Date: ${missionData.date_mission || "N/A"}
Adresse: ${missionData.adresse || "N/A"}
Demande Matériel: ${missionData.demande_materiel || "N/A"}
                  
Patient:
${patient?.nom || "N/A"} ${patient?.prenom || "N/A"}
Téléphone: ${patient?.téléphone || "N/A"}
                  
Ambulancier:
${ambulancier?.nom || "N/A"} ${ambulancier?.prenom || "N/A"}
Téléphone: ${ambulancier?.téléphone || "N/A"}
                  
Médecin:
${doctor?.nom || "N/A"} ${doctor?.prenom || "N/A"}
Téléphone: ${doctor?.téléphone || "N/A"}`;
};

export const extractMissionId = (response) => {
  const data = response?.data;
  if (!data) return null;
  return data.id || data.mission_id || data.missionId || data.mission?.id || null;
};

const resolvePatient = async (patient, patientId) => {
  if (patient) return patient;
  if (!patientId) return null;
  try {
    const patientRes = await apiClient.get(`${BASE_URL}/patients/${patientId}`);
    return patientRes.data || null;
  } catch (_) {
    return null;
  }
};

const resolveMission = async (missionId) => {
  if (!missionId) return null;
  try {
    const missionRes = await apiClient.get(`${BASE_URL}/missions/${missionId}`);
    return missionRes.data || null;
  } catch (_) {
    return null;
  }
};

export async function sendMissionCreationNotifications({ missionId, missionData, patient }) {
  const notifications = [];

  try {
    const usersRes = await apiClient.get(`${BASE_URL}/users`);
    const users = Array.isArray(usersRes.data) ? usersRes.data : [];

    if (missionData.ambulancier_id) {
      const doctor = findUser(users, missionData.doctor_id);
      const nurse = findUser(users, missionData.nurse_id);
      notifications.push({
        user_id: missionData.ambulancier_id,
        mission_id: missionId,
        message: buildAmbulancierMessage(missionData, patient, doctor, nurse),
        date_notification: formatNotificationDate(missionData.date_mission),
        type: "mission",
        statut: "nonlue",
      });
    }

    if (missionData.doctor_id) {
      const ambulancier = findUser(users, missionData.ambulancier_id);
      const nurse = findUser(users, missionData.nurse_id);
      notifications.push({
        user_id: missionData.doctor_id,
        mission_id: missionId,
        message: buildDoctorMessage(missionData, patient, ambulancier, nurse),
        date_notification: formatNotificationDate(missionData.date_mission),
        type: "mission",
        statut: "nonlue",
      });
    }

    if (missionData.nurse_id) {
      const nurse = findUser(users, missionData.nurse_id);
      if (nurse) {
        const ambulancier = findUser(users, missionData.ambulancier_id);
        const doctor = findUser(users, missionData.doctor_id);
        notifications.push({
          user_id: missionData.nurse_id,
          mission_id: missionId,
          message: buildNurseMessage(missionData, patient, ambulancier, doctor),
          date_notification: formatNotificationDate(missionData.date_mission),
          type: "mission",
          statut: "nonlue",
        });
      }
    }

    for (const notification of notifications) {
      try {
        await apiClient.post(`${BASE_URL}/notifications`, notification);
      } catch (_) {
        // Ne pas bloquer si une notification échoue
      }
    }
  } catch (_) {
    // Ne pas bloquer la création de mission si les notifications échouent
  }
}

export async function sendMissionTrackingSms({ missionId, missionData, patient, vehicle, loc }) {
  const phone = patient?.téléphone || null;
  if (!phone) return;

  const typeMission = missionData.type_mission || "ambulance";

  if (typeMission === "ambulance") {
    const trackerId = vehicle?.tracker_id || null;
    const map = {
      id: uuidv4(),
      missionid: missionId,
      loc,
      tracker_id: trackerId,
      fin: 0,
    };

    try {
      await apiClient.post(`${BASE_URL}/map`, map);
      const message = `Omnidoc Santé\nVotre ambulance est en route. Suivez sa localisation en direct ici : ${process.env.REACT_APP_BASE_URL_PROD}/prestation/${map.id}`;
      await sendSMS(phone, message);
    } catch (_) {
      // Ne pas bloquer si SMS/map échoue
    }
    return;
  }

  if (typeMission === "Consultation à domicile" && missionData.doctor_id) {
    const map = {
      id: uuidv4(),
      missionid: missionId,
      loc,
      userid: parseInt(missionData.doctor_id, 10),
      fin: 0,
    };

    try {
      await apiClient.post(`${BASE_URL}/map`, map);
      const message = `Omnidoc Santé\nVotre médecin est en route. Suivez sa localisation en direct ici : ${process.env.REACT_APP_BASE_URL_PROD}/prestation/${map.id}`;
      await sendSMS(phone, message);
    } catch (_) {}
    return;
  }

  if (typeMission === "Acte infirmier" && missionData.nurse_id) {
    const map = {
      id: uuidv4(),
      missionid: missionId,
      loc,
      userid: parseInt(missionData.nurse_id, 10),
      fin: 0,
    };

    try {
      await apiClient.post(`${BASE_URL}/map`, map);
      const message = `Omnidoc Santé\nVotre infirmier est en route. Suivez sa localisation en direct ici : ${process.env.REACT_APP_BASE_URL_PROD}/prestation/${map.id}`;
      await sendSMS(phone, message);
    } catch (_) {}
  }
}

export async function handleMissionCreationSideEffects({
  missionId,
  missionData,
  patient = null,
  vehicle = null,
  loc = null,
}) {
  if (!missionId || !missionData) return;

  const resolvedPatient =
    patient ||
    (await resolvePatient(null, missionData.patient_id));

  await sendMissionCreationNotifications({
    missionId,
    missionData,
    patient: resolvedPatient,
  });

  await sendMissionTrackingSms({
    missionId,
    missionData,
    patient: resolvedPatient,
    vehicle,
    loc,
  });
}

export async function handleDossierCreemissionSideEffects({
  creemissionResponse,
  dossier = null,
  affectationPayload = {},
  vehicle = null,
  patient = null,
}) {
  let missionId = extractMissionId(creemissionResponse);

  if (!missionId && dossier?.id) {
    try {
      const dossierRes = await apiClient.get(`${BASE_URL}/dossier/${dossier.id}`);
      const freshDossier = dossierRes.data || null;
      missionId = freshDossier?.mission_id || freshDossier?.missionId || missionId;
      dossier = freshDossier || dossier;
    } catch (_) {}
  }

  if (!missionId) return;

  const mission = await resolveMission(missionId);
  const patientId = mission?.patient_id || dossier?.patient_id || null;
  const resolvedPatient = await resolvePatient(patient || dossier?.patient, patientId);

  const missionData = {
    type_mission: mission?.type_mission || "ambulance",
    cause: mission?.cause || dossier?.cause || null,
    date_mission: mission?.date_mission || dossier?.date_mission || new Date().toISOString(),
    adresse: mission?.adresse || dossier?.adresse || null,
    demande_materiel: mission?.demande_materiel || dossier?.demande_materiel || null,
    ambulancier_id: affectationPayload.ambulancier_id || mission?.ambulancier_id || null,
    doctor_id: affectationPayload.doctor_id || mission?.doctor_id || null,
    nurse_id: affectationPayload.nurse_id || mission?.nurse_id || null,
    patient_id: patientId,
  };

  const loc = mission?.loc || dossier?.loc || null;

  await handleMissionCreationSideEffects({
    missionId,
    missionData,
    patient: resolvedPatient,
    vehicle,
    loc,
  });
}